<script setup lang="ts">
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '@/modules/commissioning/stores/deviceSpecificationStore';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import {
  getTerminalGroupingWithErrorsFromCommissionResult,
  getCommissionResultConfigValues,
} from '@/modules/commissioning/utils/error-resolver';
import type { ComponentErrorType } from '@/modules/commissioning/utils/error-constants';
import ComErrorIds from '@/modules/commissioning/components/ComErrorIds.vue';
import ComErrorCtaButtons from '@/modules/commissioning/components/ComErrorCtaButtons.vue';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';

const errorType = 'InputMissingVoltage' as ComponentErrorType;
const props = defineProps<{
  failIdsFromRoute?: string[]; // For debugging purposes only
}>();

const deviceStore = useCommissioningDeviceStore();
const deviceSpecificationStore = useDeviceSpecificationStore();
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);
const connections = deviceSpecificationStore.solshareSystemSpec.connections;

/**
 * Get the fail IDs from the commission result
 */
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const failIds = props.failIdsFromRoute ?? commissionResultFailIds[errorType] ?? [];

/**
 * Get the CTA buttons from the commission result
 */
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');
const ctaButtons = commissionResultCtaButtons[errorType] ?? [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  ERROR_CTA_BUTTONS.RECONFIGURE,
];

/**
 * Get the terminal error grouping from the commission result
 */
const terminalErrorGrouping = getTerminalGroupingWithErrorsFromCommissionResult(
  commissionResult,
  connections,
);
const InputMissingVoltageUnits = terminalErrorGrouping[errorType] ?? [];
</script>

<template>
  <ProgressFrame
    pageClass="fullfocus-page"
    titleTight
    withBackArrow
    backButtonClass="error-back-button"
  >
    <template #title><div class="error-title">Tenancy Connection(s) Missing Voltage</div></template>
    <!-- <template #subTitle><div class="error-sub-title">&nbsp;</div></template> -->

    <div v-if="failIds.length > 0" class="error-fail-ids--wrapper">
      <ComErrorIds :failIds="failIds" unique />
    </div>

    <div class="error-intro">
      <p>The connections below are not reading voltage correctly:</p>
      <ul class="error-bullet-list">
        <li v-for="(unit, i) of InputMissingVoltageUnits" :key="i">{{ unit }}</li>
      </ul>
    </div>

    <div class="error-handle-section">
      <p>
        Verify the Main Switch (Normal, Main Switch (Inverter) and SolShare AC Isolator (Grid) are
        closed.
      </p>
      <p>Measure for grid voltage on the Tenancy Connection terminal block.</p>
      <p>When there is grid voltage on the connection(s), retry commissioning.</p>
    </div>

    <ComErrorCtaButtons :ctaButtons="ctaButtons" />
  </ProgressFrame>
</template>

<style lang="less" scoped></style>
